import { z } from 'zod';
import { DATE_FORMATS, LANGUAGES, type DateFormat, type Language } from '../constants/locale';
import { CURRENCIES, type Currency } from '../money/money';

export const updateOrganizationSchema = z.object({
  name: z.string().trim().min(1).max(80),
  legalName: z.string().trim().min(1).max(160).optional(),
  kraPin: z.string().trim().min(1).max(20).optional(),
  address: z.string().trim().min(1).max(500).optional(),
  currency: z.enum(CURRENCIES).optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
  dateFormat: z.enum(DATE_FORMATS).optional(),
  language: z.enum(LANGUAGES).optional(),
});
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationSchema>;

/** What the web app shows for the company this deployment serves. Public: the login page needs it. */
export interface OrganizationProfile {
  /** False until the deployment has been given its company; the UI then shows Chukua's own branding. */
  configured: boolean;
  name: string;
  legalName: string | null;
  /** Same-origin, versioned (safe to cache forever). Null when no logo has been set. */
  logoUrl: string | null;
  /** Variant for dark surfaces; null means use `logoUrl` there too. */
  logoDarkUrl: string | null;
  currency: Currency;
  timezone: string;
  dateFormat: DateFormat;
  language: Language;
}

/** The admin-only settings screen: the public profile plus the company's tax registration. */
export interface OrganizationSettings extends OrganizationProfile {
  kraPin: string | null;
  address: string | null;
}

/** Before a deployment is given its company, both apps show Chukua's own branding and these defaults. */
export const UNCONFIGURED_ORGANIZATION: OrganizationProfile = {
  configured: false,
  name: 'Chukua',
  legalName: null,
  logoUrl: null,
  logoDarkUrl: null,
  currency: 'KES',
  timezone: 'Africa/Nairobi',
  dateFormat: 'DD/MM/YYYY',
  language: 'en',
};
